import { Grid, Paper, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../shared/hooks/useResponsive';
import { PartsList } from '../manageParts';
import { ChargesList } from '../manageCharges';
import { formatCurrency } from '@utils/numberFormatters';
import type { WorkOrderPart } from '../manageParts/types/parts.types';
import type { WorkOrderCharge } from '../manageCharges/types/charges.types';

type Props = {
  jobNumber: string;
  parts: WorkOrderPart[];
  charges?: WorkOrderCharge[];
  isPartsLoading: boolean;
  isChargesLoading?: boolean;
  isReadOnly?: boolean;
};

export const PartsAndChargesView = ({
  jobNumber,
  parts,
  charges = [],
  isPartsLoading,
  isChargesLoading = false,
  isReadOnly = false
}: Props) => {
  const { t } = useTranslation('workOrders');
  const { isDesktop } = useResponsive();

  const partsTotal =
    parts?.reduce((sum, part) => sum + (part.lineTotalCost || 0), 0) || 0;
  const chargesTotal =
    charges?.reduce((sum, charge) => sum + (charge.lineTotalCost || 0), 0) || 0;
  const combinedTotal = partsTotal + chargesTotal;

  const content = (
    <>
      <Box
        sx={{
          mb: 3,
          textAlign: 'center',
          py: 2,
          px: 3,
          backgroundColor: 'action.hover',
          borderRadius: 1
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t('partsAndCharges.totalCost')}: {formatCurrency(combinedTotal)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('partsAndCharges.parts')}: {formatCurrency(partsTotal)} |{' '}
          {t('partsAndCharges.charges')}: {formatCurrency(chargesTotal)}
        </Typography>
      </Box>
      <Grid container spacing={isDesktop ? 3 : 2}>
        <Grid item xs={12} md={6}>
          <PartsList
            parts={parts}
            isLoading={isPartsLoading}
            jobNumber={jobNumber}
            isReadOnly={isReadOnly}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ChargesList
            charges={charges}
            isLoading={isChargesLoading}
            jobNumber={jobNumber}
            isReadOnly={isReadOnly}
          />
        </Grid>
      </Grid>
    </>
  );

  if (isDesktop) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: { xs: 2, sm: 2, md: 3 },
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        {content}
      </Paper>
    );
  }

  return <Box sx={{ mt: 2, px: 1 }}>{content}</Box>;
};
